import { AuctionModel } from '../model/auction.model';
import moment from 'moment-timezone';
import { formatCountdownMs, formatLotClockDisplay } from '../auctions/auction-time-remaining-counter/shot-clock-timer-label';

export class AuctionTimeStatistics {
  private auction: AuctionModel

  constructor(auction: AuctionModel) {
    this.auction = auction;
    if (!moment.utc(auction.startTime).isValid()) throw new Error("Invalid start date.")
    if (!moment.utc(auction.endTime).isValid()) throw new Error("Invalid end date.")
  }

  public isBeforeAuction(): boolean {
    return moment.utc(this.auction.startTime).isAfter(moment.utc())
  }

  public isAfterAuctionStart(): boolean {
    return moment.utc(this.auction.startTime).isSameOrBefore(moment.utc())
  }

  public isBeforeEndOfAuction(): boolean {
    if (this.auction.type === 'SHOT_CLOCK' && this.auction.status === 'LIVE') {
      return true;
    }
    return moment.utc(this.auction.endTime).isSameOrAfter(moment.utc())
  }

  public isClosed(): boolean {
    if (this.auction.type === 'SHOT_CLOCK') {
      return this.auction.status === 'CLOSED';
    }
    return moment.utc().isSameOrAfter(moment.utc(this.auction.endTime))
  }

  public numDaysBeforeAuctionStart(): number {
    return Math.round(this.durationBeforeAuctionStart().asDays())
  }

  public numSecondsBeforeAuctionStart(): number {
    return Math.round(this.durationBeforeAuctionStart().asSeconds())
  }

  public numHoursBeforeAuctionStart(): number {
    return Math.round(this.durationBeforeAuctionStart().asHours())
  }

  public numMinutesBeforeAuctionStart(): number {
    return Math.round(this.durationBeforeAuctionStart().asMinutes())
  }

  public numMinutesSecondsBeforeAuctionStart(): string {
    return formatCountdownMs(this.durationBeforeAuctionStart().as('milliseconds'))
  }

  public numDaysBeforeAuctionEnd(): number {
    return Math.round(this.durationBeforeAuctionEnd().asDays())
  }

  public numSecondsBeforeAuctionEnd(): number {
    return moment.utc(this.clockEndTime()).diff(moment.utc(), 'seconds')
  }

  public durationBeforeAuctionStart(): moment.Duration {
    return moment.duration(moment.utc(this.auction.startTime).diff(moment.utc()))
  }

  public durationBeforeAuctionEnd(): moment.Duration {
    return moment.duration(moment.utc(this.clockEndTime()).diff(moment.utc()))
  }

  private clockEndTime(): string {
    return this.auction.clockEndTime || this.auction.endTime;
  }

  public numHoursMinutesBeforeAuctionStart(): string {
    const duration = this.durationBeforeAuctionStart()
    return `${Math.floor(duration.asHours())}h ${moment.utc(duration.asMilliseconds()).format("m")}m`
  }

  public numHoursBeforeAuctionEnd(): number {
    return this.durationBeforeAuctionEnd().asHours()
  }

  public numMinutesBeforeAuctionEnd(): number {
    return this.durationBeforeAuctionEnd().asMinutes()
  }

  public durationBeforeCatalogEnd(): moment.Duration {
    return moment.duration(moment.utc(this.auction.endTime).diff(moment.utc()))
  }

  public numMinutesSecondsBeforeCatalogEnd(): string {
    return formatCountdownMs(this.durationBeforeCatalogEnd().as('milliseconds'))
  }

  public numMinutesBeforeCatalogEnd(): number {
    return this.durationBeforeCatalogEnd().asMinutes()
  }

  public numHoursMinutesBeforeCatalogEnd(): string {
    const duration = this.durationBeforeCatalogEnd()
    const ms = Math.max(0, duration.asMilliseconds())
    return `${Math.floor(ms / 3600000)}h ${moment.utc(ms).format("m")}m`
  }

  public numDaysBeforeCatalogEnd(): number {
    return Math.round(this.durationBeforeCatalogEnd().asDays())
  }

  public numMinutesSecondsBeforeAuctionEnd(): string {
    return formatLotClockDisplay(this.durationBeforeAuctionEnd().as('milliseconds'), {
      isShotClock: this.auction.type === 'SHOT_CLOCK'
    })
  }

  public numHoursMinutesBeforeAuctionEnd(): string {
    const duration = this.durationBeforeAuctionEnd()
    return `${Math.floor(duration.asHours())}h ${moment.utc(duration.asMilliseconds()).format("m")}m`
  }

  public auctionCurrentlyActive(): boolean {
    return this.isAfterAuctionStart() && this.isBeforeEndOfAuction()
  }

  public multipleDaysRemainBeforeStartOrEnd(): boolean {
    return (this.isBeforeAuction() && this.numHoursBeforeAuctionStart() > 24) || (this.auctionCurrentlyActive() && this.numHoursBeforeAuctionEnd() > 24)
  }

  public closingExtensionActive(): boolean {
    return this.auction.closingExtensionEnabled && this.auction.closingExtensionTriggered
  }
}